// Merkenbalk (sections/brand-logo-bar.liquid): maakt van logo's uit heel
// verschillende bestanden één consistent lint.
//
// De bestanden verschillen te veel om met CSS alleen gelijk te trekken: het ene
// logo staat klein in een vierkant vol witruimte, het andere is geel of groen
// en wordt in grijstinten bijna onzichtbaar, een jpg heeft een witte
// achtergrond. Daarom wordt elk logo na het laden op een canvas:
//
// 1. bijgesneden tot wat er echt getekend staat (witruimte en transparantie
//    weg);
// 2. in één donkere tint gezet: elke pixel die niet (bijna) wit of transparant
//    is, telt als inkt -- geel en groen worden even krachtig als zwart. Of, met
//    data-logo-tone="grayscale", in grijstinten: dan blijft de tekening van een
//    clublogo of wapenschild leesbaar;
// 3. op gelijke oppervlakte geschaald in plaats van op gelijke hoogte, zodat
//    een vierkant logo niet nietig oogt naast een lang woordmerk.
//
// Lukt dat niet (oude browser, afbeelding niet leesbaar), dan blijft de
// grijze CSS-versie staan.
(() => {
  const INK = [17, 17, 17];
  const cache = new Map();

  // Doeloppervlakte in px² en grenzen per logo; op mobiel kleiner. In een vaste
  // rij staan er maar een handvol logo's, die mogen groter dan in het lint.
  const sizing = (img) => {
    const desktop = window.matchMedia('(min-width: 768px)').matches;

    if (img.closest('.brand-logo-bar--static')) {
      return desktop
        ? { area: 7600, maxHeight: 72, maxWidth: 200 }
        : { area: 3800, maxHeight: 50, maxWidth: 115 };
    }

    // Vaste rij op desktop, lint op mobiel: in het lint is er geen
    // breedtegrens per kolom, dus mogen ze daar ruimer dan in een gewoon lint.
    if (img.closest('.brand-logo-bar--static-desktop')) {
      return desktop
        ? { area: 7600, maxHeight: 72, maxWidth: 200 }
        : { area: 4200, maxHeight: 52, maxWidth: 140 };
    }

    // Referenties (grijstinten) in een lint: clublogo's en wapenschilden zijn
    // vierkant en gedetailleerd; op de kleine merkenmaat werden het vlekjes.
    // Bijna even groot als in de vaste rij, op mobiel gelijk aan het lint
    // hierboven.
    if (img.closest('[data-logo-tone="grayscale"]')) {
      return desktop
        ? { area: 4180, maxHeight: 55, maxWidth: 152 }
        : { area: 2510, maxHeight: 41, maxWidth: 109 };
    }

    return desktop
      ? { area: 3400, maxHeight: 40, maxWidth: 140 }
      : { area: 2400, maxHeight: 32, maxWidth: 112 };
  };

  // tone "ink": één vlakke donkere tint, voor woordmerken. tone "grayscale": de
  // licht-donkerverdeling blijft, alleen de kleur verdwijnt -- voor clublogo's en
  // wapenschilden, waarvan anders alleen een donkere vlek overbleef.
  const process = (img, tone) => {
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(img, 0, 0);

    const image = context.getImageData(0, 0, width, height);
    const { data } = image;
    let top = height;
    let left = width;
    let right = -1;
    let bottom = -1;

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const i = (y * width + x) * 4;
        const luminance = (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
        // Bijna wit telt niet als inkt; daartussen een zachte overgang, zodat
        // de randen van letters glad blijven.
        // Halfdoorzichtige randen (gloed, schaduw) worden scherp afgesneden:
        // vlakke letters en vormen zijn volledig dekkend en blijven zoals ze zijn.
        const alpha = Math.min(1, Math.max(0, (data[i + 3] / 255 - 0.35) / 0.4));
        const ink = Math.min(1, Math.max(0, (0.96 - luminance) / 0.16)) * alpha;

        if (tone === 'grayscale') {
          // Iets donkerder dan de echte grijswaarde, zodat lichte vlakken niet
          // wegvallen tegen de witte pagina.
          const gray = Math.round(luminance * 200);
          data[i] = gray;
          data[i + 1] = gray;
          data[i + 2] = gray;
        } else {
          data[i] = INK[0];
          data[i + 1] = INK[1];
          data[i + 2] = INK[2];
        }
        data[i + 3] = Math.round(ink * 255);

        if (ink > 0.1) {
          top = Math.min(top, y);
          bottom = Math.max(bottom, y);
          left = Math.min(left, x);
          right = Math.max(right, x);
        }
      }
    }

    if (right < 0) {
      return null;
    }

    const trimmedWidth = right - left + 1;
    const trimmedHeight = bottom - top + 1;
    const output = document.createElement('canvas');
    output.width = trimmedWidth;
    output.height = trimmedHeight;
    output.getContext('2d').putImageData(image, -left, -top);

    return { src: output.toDataURL('image/png'), ratio: trimmedWidth / trimmedHeight };
  };

  const apply = (img, result) => {
    const { area, maxHeight, maxWidth } = sizing(img);
    let height = Math.min(maxHeight, Math.sqrt(area / result.ratio));
    let width = height * result.ratio;

    if (width > maxWidth) {
      width = maxWidth;
      height = width / result.ratio;
    }

    img.src = result.src;
    img.style.width = `${Math.round(width)}px`;
    img.style.height = `${Math.round(height)}px`;
    img.classList.add('is-normalized');
  };

  const normalize = (img) => {
    if (img.dataset.normalized) {
      return;
    }

    const toneHolder = img.closest('[data-logo-tone]');
    const tone = toneHolder ? toneHolder.dataset.logoTone : 'ink';
    const key = `${tone}:${img.currentSrc || img.src}`;

    if (!cache.has(key)) {
      try {
        cache.set(key, process(img, tone));
      } catch (error) {
        // Niet leesbaar (bv. geen CORS): de grijze CSS-versie blijft staan.
        cache.set(key, null);
      }
    }

    const result = cache.get(key);
    img.dataset.normalized = 'true';

    if (result) {
      apply(img, result);
    }
  };

  document.querySelectorAll('.js-brand-logo').forEach((img) => {
    if (img.complete && img.naturalWidth) {
      normalize(img);
    } else {
      img.addEventListener('load', () => normalize(img), { once: true });
    }
  });
})();
